import { useEffect, useState } from "react";
import type { TemplateMilestoneSpec, TemplateRuleSpec, TemplateSpec } from "../lib/types";
import { ErrorBanner } from "./ui";

const BLANK_RULE: TemplateRuleSpec = {
  name: "",
  priority: 10,
  categories: [],
  merchantPatterns: [],
  channel: "ANY",
  international: null,
  rate: 1,
  unitAmount: 100,
  roundDown: true,
  minAmount: null,
  capType: "NONE",
  capValue: null,
  capPeriod: "MONTH",
  capAppliesTo: "ALL",
  capGroup: null,
  capOverflow: "BASE",
  isExclusion: false,
  milestoneEligible: true,
  notes: null,
};

const BLANK_MILESTONE: TemplateMilestoneSpec = {
  name: "",
  type: "SPEND_MILESTONE",
  metric: "SPEND",
  minTransactionAmount: null,
  threshold: 100000,
  period: "ANNIVERSARY_YEAR",
  rewardDescription: "",
  rewardValue: 0,
  rewardPoints: 0,
};

export const BLANK_SPEC: TemplateSpec = {
  name: "",
  issuer: "",
  network: "VISA",
  annualFee: 0,
  joiningFee: 0,
  feeWaiverSpend: null,
  forexMarkupPct: 3.5,
  summary: "",
  currency: { name: "", valuePerPoint: 1, bestValuePerPoint: null, expiryMonths: null, isCashback: false, notes: null },
  rules: [{ ...BLANK_RULE, name: "Base rate", priority: 0 }],
  milestones: [],
};

const num = (v: string): number => (v.trim() === "" ? 0 : Number(v));
const nullableNum = (v: string): number | null => (v.trim() === "" ? null : Number(v));

/**
 * Edits a card template. Every figure the rewards engine reads is exposed here
 * — the form for day-to-day corrections, the JSON view for pasting a whole set
 * in or out.
 */
export function TemplateEditor({
  spec,
  onChange,
  categories,
  networks,
}: {
  spec: TemplateSpec;
  onChange: (spec: TemplateSpec) => void;
  categories: { key: string; label: string }[];
  networks: string[];
}) {
  const [mode, setMode] = useState<"form" | "json">("form");
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "json") setJson(JSON.stringify(spec, null, 2));
  }, [mode, spec]);

  function applyJson(text: string) {
    setJson(text);
    try {
      onChange(JSON.parse(text) as TemplateSpec);
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : "That isn't valid JSON");
    }
  }

  const set = (patch: Partial<TemplateSpec>) => onChange({ ...spec, ...patch });
  const setRule = (i: number, patch: Partial<TemplateRuleSpec>) =>
    set({ rules: spec.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const setMilestone = (i: number, patch: Partial<TemplateMilestoneSpec>) =>
    set({ milestones: spec.milestones.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });

  return (
    <div className="space-y-5">
      <div className="flex gap-1 border-b border-ink-200">
        {(["form", "json"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-xs font-medium ${
              mode === m ? "border-accent-600 text-accent-800" : "border-transparent text-ink-500 hover:text-ink-800"
            }`}
          >
            {m === "form" ? "Form" : "JSON"}
          </button>
        ))}
      </div>

      {mode === "json" ? (
        <div>
          <textarea
            className="input h-96 font-mono text-xs"
            value={json}
            onChange={(e) => applyJson(e.target.value)}
            spellCheck={false}
          />
          {jsonError ? (
            <ErrorBanner message={jsonError} />
          ) : (
            <p className="mt-1 text-xs text-ink-500">
              Edited live — switch back to Form to see it, or Save to store it. Validated on save.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* ---------------------------------------------------- the card */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Card</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="label">Name</label>
                <input className="input" value={spec.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div>
                <label className="label">Issuer</label>
                <input className="input" value={spec.issuer} onChange={(e) => set({ issuer: e.target.value })} />
              </div>
              <div>
                <label className="label">Network</label>
                <select className="input" value={spec.network} onChange={(e) => set({ network: e.target.value })}>
                  {[...new Set([spec.network, ...networks])].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Annual fee (₹)</label>
                <input className="input tnum" type="number" min={0} value={spec.annualFee}
                  onChange={(e) => set({ annualFee: num(e.target.value) })} />
              </div>
              <div>
                <label className="label">Joining fee (₹)</label>
                <input className="input tnum" type="number" min={0} value={spec.joiningFee}
                  onChange={(e) => set({ joiningFee: num(e.target.value) })} />
              </div>
              <div>
                <label className="label">Fee waived at spend (₹)</label>
                <input className="input tnum" type="number" min={0} placeholder="No waiver"
                  value={spec.feeWaiverSpend ?? ""}
                  onChange={(e) => set({ feeWaiverSpend: nullableNum(e.target.value) })} />
              </div>
              <div>
                <label className="label">Forex markup (%)</label>
                <input className="input tnum" type="number" min={0} step="0.1" value={spec.forexMarkupPct}
                  onChange={(e) => set({ forexMarkupPct: num(e.target.value) })} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Summary</label>
                <input className="input" value={spec.summary} onChange={(e) => set({ summary: e.target.value })}
                  placeholder="e.g. 5 RP per ₹150, 10X on SmartBuy" />
              </div>
            </div>
          </section>

          {/* ------------------------------------------------ the programme */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Reward programme</h3>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <label className="label">Name</label>
                <input className="input" value={spec.currency.name}
                  onChange={(e) => set({ currency: { ...spec.currency, name: e.target.value } })} />
              </div>
              <div>
                <label className="label">₹ per point</label>
                <input className="input tnum" type="number" min={0} step="0.01" value={spec.currency.valuePerPoint}
                  onChange={(e) => set({ currency: { ...spec.currency, valuePerPoint: num(e.target.value) } })} />
              </div>
              <div>
                <label className="label">Best ₹ per point</label>
                <input className="input tnum" type="number" min={0} step="0.01"
                  value={spec.currency.bestValuePerPoint ?? ""} placeholder="Optional"
                  onChange={(e) => set({ currency: { ...spec.currency, bestValuePerPoint: nullableNum(e.target.value) } })} />
              </div>
              <div>
                <label className="label">Expires after (months)</label>
                <input className="input tnum" type="number" min={1} value={spec.currency.expiryMonths ?? ""}
                  placeholder="Never"
                  onChange={(e) => set({ currency: { ...spec.currency, expiryMonths: nullableNum(e.target.value) } })} />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                    checked={spec.currency.isCashback}
                    onChange={(e) => set({ currency: { ...spec.currency, isCashback: e.target.checked } })} />
                  Cashback
                </label>
              </div>
            </div>
          </section>

          {/* ---------------------------------------------------- the rules */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                Earn rules ({spec.rules.length})
              </h3>
              <button type="button" className="btn-secondary px-3 py-1 text-xs"
                onClick={() => set({ rules: [...spec.rules, { ...BLANK_RULE, name: `Rule ${spec.rules.length + 1}` }] })}>
                Add rule
              </button>
            </div>

            <div className="space-y-3">
              {spec.rules.map((rule, i) => (
                <div key={i} className="rounded-lg border border-ink-200 p-3">
                  <div className="grid gap-2 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <label className="label text-xs">Rule name</label>
                      <input className="input" value={rule.name} onChange={(e) => setRule(i, { name: e.target.value })} />
                    </div>
                    <div>
                      <label className="label text-xs">Priority</label>
                      <input className="input tnum" type="number" value={rule.priority}
                        onChange={(e) => setRule(i, { priority: num(e.target.value) })} />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-1.5 text-xs text-ink-700">
                        <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                          checked={rule.isExclusion} onChange={(e) => setRule(i, { isExclusion: e.target.checked })} />
                        Excludes
                      </label>
                    </div>
                    <div className="flex items-end justify-end pb-2">
                      <button type="button" className="text-xs text-red-700 underline"
                        onClick={() => set({ rules: spec.rules.filter((_, idx) => idx !== i) })}>
                        Remove
                      </button>
                    </div>
                  </div>

                  {!rule.isExclusion && (
                    <div className="mt-2 grid gap-2 sm:grid-cols-6">
                      <div>
                        <label className="label text-xs" title="Earn a multiple of the card's base rate instead of a fixed rate — 10 for 10X">
                          × base (optional)
                        </label>
                        <input className="input tnum" type="number" min={0} step="0.5" placeholder="Fixed" value={rule.multiplier ?? ""}
                          aria-label={`Multiple of the base rate for ${rule.name || `rule ${i + 1}`}`}
                          onChange={(e) => setRule(i, { multiplier: e.target.value === "" ? null : num(e.target.value) })} />
                        {rule.multiplier != null && (
                          <span className="text-xs text-ink-500">
                            {rule.multiplierIncludesBase === false ? "on top of the base" : "incl. the base"}
                          </span>
                        )}
                      </div>
                      <div>
                        <label className="label text-xs" title="The total the rule earns, base included — not the extra on top">Total pts (incl. base)</label>
                        <input className="input tnum" type="number" min={0} step="0.1" value={rule.rate}
                          disabled={rule.multiplier != null}
                          title={rule.multiplier != null ? "Worked out from the base rate for a multiple" : undefined}
                          aria-label={`Points for ${rule.name || `rule ${i + 1}`}`}
                          onChange={(e) => setRule(i, { rate: num(e.target.value) })} />
                      </div>
                      <div>
                        <label className="label text-xs">Per ₹</label>
                        <input className="input tnum" type="number" min={1} value={rule.unitAmount}
                          disabled={rule.multiplier != null}
                          aria-label={`Spend unit for ${rule.name || `rule ${i + 1}`}`}
                          onChange={(e) => setRule(i, { unitAmount: num(e.target.value) })} />
                      </div>
                      <div>
                        <label className="label text-xs">Cap type</label>
                        <select className="input" value={rule.capType} onChange={(e) => setRule(i, { capType: e.target.value })}>
                          <option value="NONE">No cap</option>
                          <option value="POINTS">Max points</option>
                          <option value="SPEND">Max spend</option>
                        </select>
                      </div>
                      <div>
                        <label className="label text-xs">Cap value</label>
                        <input className="input tnum" type="number" min={0} disabled={rule.capType === "NONE"}
                          aria-label={`Cap value for ${rule.name || `rule ${i + 1}`}`}
                          value={rule.capValue ?? ""} onChange={(e) => setRule(i, { capValue: nullableNum(e.target.value) })} />
                      </div>
                      <div>
                        <label className="label text-xs">Resets</label>
                        <select className="input" disabled={rule.capType === "NONE"} value={rule.capPeriod}
                          onChange={(e) => setRule(i, { capPeriod: e.target.value })}>
                          {["MONTH", "STATEMENT", "QUARTER", "YEAR"].map((p) => (
                            <option key={p} value={p}>{p === "STATEMENT" ? "Statement" : p.charAt(0) + p.slice(1).toLowerCase()}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="label text-xs">At the cap</label>
                        <select className="input" disabled={rule.capType === "NONE"}
                          value={rule.capAppliesTo === "BONUS" ? "BONUS" : rule.capOverflow === "NOTHING" ? "NOTHING" : "BASE"}
                          aria-label={`What happens at the cap for ${rule.name || `rule ${i + 1}`}`}
                          onChange={(e) =>
                            setRule(i, {
                              capAppliesTo: e.target.value === "BONUS" ? "BONUS" : "ALL",
                              capOverflow: e.target.value === "NOTHING" ? "NOTHING" : "BASE",
                            })
                          }>
                          <option value="BONUS">Bonus stops</option>
                          <option value="BASE">Base rate</option>
                          <option value="NOTHING">Nothing</option>
                        </select>
                      </div>
                      <div>
                        <label className="label text-xs">Shared cap</label>
                        <input className="input" disabled={rule.capType === "NONE"} value={rule.capGroup ?? ""} placeholder="None"
                          aria-label={`Shared cap name for ${rule.name || `rule ${i + 1}`}`}
                          onChange={(e) => setRule(i, { capGroup: e.target.value || null })} />
                      </div>
                      <div>
                        <label className="label text-xs">Min txn (₹)</label>
                        <input className="input tnum" type="number" min={0} value={rule.minAmount ?? ""} placeholder="Any"
                          onChange={(e) => setRule(i, { minAmount: nullableNum(e.target.value) })} />
                      </div>
                    </div>
                  )}

                  <div className="mt-2 grid gap-2 sm:grid-cols-4">
                    <div className="sm:col-span-2">
                      <label className="label text-xs">Categories (blank = all)</label>
                      <select
                        multiple
                        className="input h-20 text-xs"
                        value={rule.categories}
                        onChange={(e) =>
                          setRule(i, { categories: [...e.target.selectedOptions].map((o) => o.value) })
                        }
                      >
                        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label text-xs">Merchant keywords</label>
                      <input className="input" value={rule.merchantPatterns.join(", ")}
                        placeholder="amazon, swiggy"
                        onChange={(e) => setRule(i, { merchantPatterns: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
                    </div>
                    <div>
                      <label className="label text-xs">Channel</label>
                      <select className="input" value={rule.channel} onChange={(e) => setRule(i, { channel: e.target.value })}>
                        <option value="ANY">Any</option>
                        <option value="ONLINE">Online only</option>
                        <option value="OFFLINE">In store only</option>
                      </select>
                      {rule.isExclusion && (
                        <label className="mt-2 flex items-center gap-1.5 text-xs text-ink-700">
                          <input type="checkbox" className="h-3.5 w-3.5 rounded border-ink-300 text-accent-600"
                            checked={rule.milestoneEligible}
                            onChange={(e) => setRule(i, { milestoneEligible: e.target.checked })} />
                          Still counts for milestones
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ----------------------------------------------- the milestones */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                Milestones ({spec.milestones.length})
              </h3>
              <button type="button" className="btn-secondary px-3 py-1 text-xs"
                onClick={() => set({ milestones: [...spec.milestones, { ...BLANK_MILESTONE }] })}>
                Add milestone
              </button>
            </div>
            <p className="mb-2 text-xs text-ink-500">
              The annual fee waiver is generated from the waiver threshold above — no need to add it here.
            </p>

            <div className="space-y-3">
              {spec.milestones.map((m, i) => (
                <div key={i} className="grid gap-2 rounded-lg border border-ink-200 p-3 sm:grid-cols-6">
                  <div className="sm:col-span-2">
                    <label className="label text-xs">Name</label>
                    <input className="input" value={m.name} onChange={(e) => setMilestone(i, { name: e.target.value })} />
                  </div>
                  <div>
                    <label className="label text-xs">Measured by</label>
                    <select className="input" value={m.metric} onChange={(e) => setMilestone(i, { metric: e.target.value })}>
                      <option value="SPEND">Amount</option>
                      <option value="TRANSACTION_COUNT">Txn count</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-xs">{m.metric === "TRANSACTION_COUNT" ? "Transactions" : "Threshold (₹)"}</label>
                    <input className="input tnum" type="number" min={1} value={m.threshold}
                      onChange={(e) => setMilestone(i, { threshold: num(e.target.value) })} />
                  </div>
                  <div>
                    <label className="label text-xs">Period</label>
                    <select className="input" value={m.period} onChange={(e) => setMilestone(i, { period: e.target.value })}>
                      <option value="ANNIVERSARY_YEAR">Anniversary yr</option>
                      <option value="CALENDAR_YEAR">Calendar yr</option>
                      <option value="QUARTER">Quarter</option>
                      <option value="MONTH">Month</option>
                    </select>
                  </div>
                  <div className="flex items-end justify-end pb-2">
                    <button type="button" className="text-xs text-red-700 underline"
                      onClick={() => set({ milestones: spec.milestones.filter((_, idx) => idx !== i) })}>
                      Remove
                    </button>
                  </div>

                  <div className="sm:col-span-3">
                    <label className="label text-xs">What you get</label>
                    <input className="input" value={m.rewardDescription}
                      onChange={(e) => setMilestone(i, { rewardDescription: e.target.value })} />
                  </div>
                  <div>
                    <label className="label text-xs">Worth (₹)</label>
                    <input className="input tnum" type="number" min={0} value={m.rewardValue}
                      onChange={(e) => setMilestone(i, { rewardValue: num(e.target.value) })} />
                  </div>
                  <div>
                    <label className="label text-xs">Bonus points</label>
                    <input className="input tnum" type="number" min={0} value={m.rewardPoints}
                      onChange={(e) => setMilestone(i, { rewardPoints: num(e.target.value) })} />
                  </div>
                  {m.metric === "TRANSACTION_COUNT" && (
                    <div>
                      <label className="label text-xs">Each txn ≥ (₹)</label>
                      <input className="input tnum" type="number" min={0} value={m.minTransactionAmount ?? ""}
                        onChange={(e) => setMilestone(i, { minTransactionAmount: nullableNum(e.target.value) })} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
